import { DashboardWidgetContainer } from "@sanity/dashboard";
import { Badge, Box, Card, Flex, Select, Spinner, Stack } from "@sanity/ui";
import allPageBuilderSections from "../../common/allPageBuilderSections";
import { ChangeEvent, useCallback, useDeferredValue, useState } from "react";
import {
  SanityComponentSearchResult,
  useSanityComponentSearch
} from "./useSanityComponentSearch";
import {
  IntentButton,
  Preview,
  getPublishedId,
  getSchemaTypeTitle,
  useClient,
  useSchema
} from "sanity";
import { isPresent } from "../../utils/checks";
import { getComponentOptions } from "./componentOptions";

const SearchPanel: React.FC = () => {
  const schema = useSchema();

  const [selectedComponentType, setSelectedComponentType] =
    useState<string>("");
  const deferredSelectedComponentType = useDeferredValue(selectedComponentType);
  const [selectedOptionKey, setSelectedOptionKey] = useState<string>("");
  const [selectedValueIndex, setSelectedValueIndex] = useState<string>("");
  const client = useClient({ apiVersion: "2023-06-05" }).withConfig({
    // raw keeps drafts and published apart, to tell which version has the section
    perspective: "raw"
  });

  const pageBuilderComponents = allPageBuilderSections()
    .map((c) => {
      const type = schema.get(c.type);
      if (!type) return undefined;

      return {
        type: c.type,
        name: getSchemaTypeTitle(type)
      };
    })
    .filter(isPresent);

  const componentOptions = getComponentOptions(
    schema.get(selectedComponentType)
  );
  const selectedOption = componentOptions.find(
    (o) => o.key === selectedOptionKey
  );
  const selectedValue =
    selectedValueIndex === ""
      ? undefined
      : selectedOption?.values[Number(selectedValueIndex)];

  const results = useSanityComponentSearch(
    deferredSelectedComponentType,
    selectedOption && selectedValue
      ? {
          ...(selectedOption.group && { group: selectedOption.group }),
          field: selectedOption.field,
          value: selectedValue.value
        }
      : undefined,
    client
  );

  const onSelect = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedComponentType(value);
    setSelectedOptionKey("");
    setSelectedValueIndex("");
  }, []);

  const onOptionSelect = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedOptionKey(e.target.value);
    setSelectedValueIndex("");
  }, []);

  const onValueSelect = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedValueIndex(e.target.value);
  }, []);

  return (
    <DashboardWidgetContainer header="Page Builder Components">
      <Stack padding={4} gap={[3, 3, 4, 5]}>
        <Select onChange={onSelect}>
          <option value=""></option>
          {pageBuilderComponents.map((c) => (
            <option key={c.type} value={c.type}>
              {c.name}
            </option>
          ))}
        </Select>
        {componentOptions.length > 0 && (
          <Select
            aria-label="Filter by option"
            onChange={onOptionSelect}
            value={selectedOptionKey}
          >
            <option value="">All options</option>
            {componentOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.title}
              </option>
            ))}
          </Select>
        )}
        {selectedOption && (
          <Select
            aria-label="Filter by value"
            onChange={onValueSelect}
            value={selectedValueIndex}
          >
            <option value="">Any value</option>
            {selectedOption.values.map((v, index) => (
              <option key={v.title} value={index}>
                {v.title}
              </option>
            ))}
          </Select>
        )}
      </Stack>
      <Stack padding={4} gap={[3, 3, 4, 5]}>
        <Card>
          {results.isError && <div>Error</div>}
          {results.isLoading && (
            <Card padding={4}>
              <Flex justify="center">
                <Spinner muted />
              </Flex>
            </Card>
          )}
          {results.isSuccess && results.data.length == 0 && (
            <div>Could not locate any documents</div>
          )}
          <Stack gap={2}>
            {results.data &&
              results.data.map((doc) => (
                <DocumentItem key={doc._id} doc={doc} />
              ))}
          </Stack>
        </Card>
      </Stack>
    </DashboardWidgetContainer>
  );
};

export default SearchPanel;

const DocumentItem: React.FC<{ doc: SanityComponentSearchResult }> = ({
  doc
}) => {
  const schema = useSchema();
  const type = schema.get(doc._type);
  return (
    <Card flex={1}>
      <IntentButton
        intent="edit"
        target="_blank"
        mode="bleed"
        //padding={1}
        //radius={0}
        params={{
          type: doc._type,
          id: getPublishedId(doc._id),
          ...(doc.sectionKey && { path: `sections[_key=="${doc.sectionKey}"]` })
        }}
        style={{ width: "100%" }}
        // a tooltip wraps the button in an inline-flex span, which breaks full width
        tooltipProps={null}
      >
        <Flex align="center" gap={2}>
          <Box flex={1}>
            {type ? (
              <Preview
                layout="default"
                schemaType={type}
                value={doc}
                key={doc._id}
              />
            ) : (
              "Schema-type missing"
            )}
          </Box>
          {doc.inPublished && <Badge tone="positive">Published</Badge>}
          {doc.inDraft && <Badge tone="caution">Draft</Badge>}
        </Flex>
      </IntentButton>
    </Card>
  );
};
